import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Linkedin, Instagram, Twitter, MapPin, Mail } from "lucide-react";
import { subscribeNewsletter } from "../lib/api";
import { toast } from "sonner";

const LINKS = {
  Company: [
    { label: "About StartINC", to: "/#why" },
    { label: "Resource Center", to: "/resources" },
    { label: "FAQ", to: "/resources" },
    { label: "Careers", to: "/resources" },
  ],
  Services: [
    { label: "Mainland Setup", to: "/company-formation/mainland" },
    { label: "Freezone Setup", to: "/company-formation/freezone" },
    { label: "Visa & Residency", to: "/visa-services" },
    { label: "Corporate Banking", to: "/banking" },
  ],
  Legal: [
    { label: "Terms of Service", to: "/resources" },
    { label: "Privacy Policy", to: "/resources" },
    { label: "Corporate Tax Notice", to: "/resources" },
  ],
};

export default function Footer() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (!email) return;
    setSending(true);
    try {
      await subscribeNewsletter(email);
      toast.success("You're in. Expect UAE Corporate Tax updates once a month.");
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail[0]?.msg : detail || "Couldn't subscribe.");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="relative bg-[#050917] text-white">
      <div className="h-px w-full bg-gradient-to-r from-transparent via-[#3366FF]/40 to-transparent" />
      <div className="container-x py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Left: brand + newsletter */}
        <div className="lg:col-span-5">
          <Link to="/" className="font-heading text-3xl font-light tracking-tight">
            Start<span className="font-black">INC</span>
            <span className="text-[#6B8AFF]">.</span>
          </Link>
          <p className="mt-5 text-slate-300/80 max-w-sm leading-relaxed">
            Corporate services, UAE incorporation and government liaison — delivered
            with the discipline of a private bank.
          </p>

          <form onSubmit={onSubscribe} className="mt-8 relative max-w-md" data-testid="newsletter-form">
            <label className="overline text-white/60 block mb-3">
              Stay updated on UAE Corporate Tax
            </label>
            <div className="relative">
              <input
                data-testid="newsletter-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@company.com"
                className="w-full rounded-sm bg-white/5 border border-white/20 text-white placeholder-white/40 pl-3 pr-14 py-3 focus:outline-none focus:border-[#6B8AFF]/50 focus:ring-2 focus:ring-[#3366FF]/20"
                required
              />
              <button
                data-testid="newsletter-submit"
                type="submit"
                disabled={sending}
                className="absolute right-1.5 top-1.5 bottom-1.5 px-3 rounded-sm bg-[#3366FF] text-[#002366] font-semibold hover:bg-[#2A55D9] disabled:opacity-60 inline-flex items-center gap-1"
              >
                {sending ? "…" : <ArrowRight className="w-4 h-4" />}
              </button>
            </div>
          </form>

          <div className="mt-8 flex gap-3">
            {[{ I: Linkedin, t: "linkedin" }, { I: Instagram, t: "instagram" }, { I: Twitter, t: "twitter" }].map(
              ({ I, t }) => (
                <a
                  key={t}
                  href={`#${t}`}
                  aria-label={t}
                  data-testid={`social-${t}`}
                  className="w-10 h-10 rounded-sm border border-white/15 inline-flex items-center justify-center hover:border-[#6B8AFF]/50 hover:text-[#6B8AFF] transition-colors"
                >
                  <I className="w-4 h-4" />
                </a>
              )
            )}
          </div>
        </div>

        {/* Links */}
        <div className="lg:col-span-5 grid grid-cols-2 sm:grid-cols-3 gap-8">
          {Object.entries(LINKS).map(([group, items]) => (
            <div key={group}>
              <p className="overline text-[#6B8AFF] mb-5">{group}</p>
              <ul className="space-y-3">
                {items.map((it) => (
                  <li key={it.label}>
                    <Link
                      to={it.to}
                      className="text-sm text-slate-300/85 hover:text-white link-underline"
                    >
                      {it.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Contact */}
        <div className="lg:col-span-2">
          <p className="overline text-[#6B8AFF] mb-5">Contact</p>
          <ul className="space-y-4 text-sm text-slate-300/85">
            <li className="flex gap-3">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#6B8AFF]" />
              <span>
                Sharjah Media City Free Zone
                <br /> Al Messaned, UAE
              </span>
            </li>
            <li className="flex gap-3">
              <Mail className="w-4 h-4 mt-0.5 shrink-0 text-[#6B8AFF]" />
              <span>contact@startinc.ae</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="container-x border-t border-white/10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
        <p className="text-xs text-slate-400">
          © {new Date().getFullYear()} StartINC Corporate Services. License No: FZCO-XXXXXX.
        </p>
        <p className="text-xs text-slate-500">
          Crafted in Dubai · Serving founders in 40+ countries.
        </p>
      </div>
    </footer>
  );
}
